import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Spinner,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Alert,
  Badge,
} from "reactstrap";
import Swal from "sweetalert2";
import {
  getAdminCommissionStructures,
  createAdminCommissionStructure,
  updateAdminCommissionStructure,
  deleteAdminCommissionStructure,
} from "../../../slices/admin/thunk";
import { formatCentsToDollars } from "../../../utils/utils";
import { DASHBOARD_USER_ROLES } from "../../../constants";

const AdminCommissionStructuresModal = ({ isOpen, setIsOpen, currentUser }) => {
  const dispatch = useDispatch();
  const [commissionStructures, setCommissionStructures] = useState([]);

  // Admin access validation
  const isCurrentUserAdmin = currentUser?.role === DASHBOARD_USER_ROLES.ADMIN;
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingStructure, setEditingStructure] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    national: "",
    ga: "",
    broker: "",
    nationalId: "",
    dashboardUserId: "",
    isDefault: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [dashboardUsers, setDashboardUsers] = useState([]);

  const resetForm = () => {
    setFormData({
      name: "",
      national: "",
      ga: "",
      broker: "",
      nationalId: "",
      dashboardUserId: "",
      isDefault: false,
    });
    setFormErrors({});
    setEditingStructure(null);
    setShowCreateForm(false);
  };

  const fetchCommissionStructures = async () => {
    setLoading(true);
    try {
      const response = await dispatch(getAdminCommissionStructures({}));
      if (response.payload?.commissionStructures) {
        setCommissionStructures(response.payload.commissionStructures);
      }
    } catch (error) {
      console.error("Error fetching commission structures:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch commission structures",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Mock dashboard users - in real implementation, fetch from API
  useEffect(() => {
    // This would typically be an API call to get dashboard users
    setDashboardUsers([
      { id: "user1", firstName: "John", lastName: "Doe", role: "national", email: "john@example.com" },
      { id: "user2", firstName: "Jane", lastName: "Smith", role: "ga", email: "jane@example.com" },
      { id: "user3", firstName: "Bob", lastName: "Johnson", role: "broker", email: "bob@example.com" },
    ]);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchCommissionStructures();
    }
  }, [isOpen]);

  const handleClose = () => {
    resetForm();
    setIsOpen(false);
  };

  const validateForm = () => {
    const errors = {};
    const { name, national, ga, broker, nationalId, dashboardUserId } = formData;

    if (!name.trim()) errors.name = "Name is required";
    if (!national || national < 0) errors.national = "National amount must be >= 0";
    if (!ga || ga < 0) errors.ga = "GA amount must be >= 0";
    if (!broker || broker < 0) errors.broker = "Broker amount must be >= 0";
    if (!nationalId) errors.nationalId = "National user is required";
    if (!dashboardUserId) errors.dashboardUserId = "Dashboard user is required";

    const total = parseFloat(national || 0) + parseFloat(ga || 0) + parseFloat(broker || 0);
    if (total !== 1000) {
      errors.total = "Total commission amounts must equal 1000 cents ($10.00)";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const structureData = {
        ...formData,
        national: parseFloat(formData.national),
        ga: parseFloat(formData.ga),
        broker: parseFloat(formData.broker),
      };

      if (editingStructure) {
        await dispatch(updateAdminCommissionStructure({
          structureId: editingStructure.id,
          structureData,
        }));
        Swal.fire({
          title: "Success!",
          text: "Commission structure updated successfully",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        await dispatch(createAdminCommissionStructure(structureData));
        Swal.fire({
          title: "Success!",
          text: "Commission structure created successfully",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      await fetchCommissionStructures();
      resetForm();
    } catch (error) {
      console.error("Error saving commission structure:", error);
      Swal.fire({
        title: "Error",
        text: `Failed to ${editingStructure ? "update" : "create"} commission structure`,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (structure) => {
    setFormData({
      name: structure.name,
      national: structure.national.toString(),
      ga: structure.ga.toString(),
      broker: structure.broker.toString(),
      nationalId: structure.nationalId,
      dashboardUserId: structure.dashboardUserId,
      isDefault: structure.isDefault || false,
    });
    setEditingStructure(structure);
    setShowCreateForm(true);
  };

  const handleDelete = async (structure) => {
    const result = await Swal.fire({
      title: "Delete Commission Structure",
      html: `<p>Are you sure you want to delete "<strong>${structure.name}</strong>"?</p>
             <p>Any users assigned to this structure will revert to the default structure.</p>
             <label>
               <input type="checkbox" id="deleteCheckbox" /> I understand the consequences
             </label>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        const checkbox = document.getElementById("deleteCheckbox");
        if (!checkbox.checked) {
          Swal.showValidationMessage("You must check the box to confirm.");
          return false;
        }
        return true;
      },
      didOpen: () => {
        const confirmButton = Swal.getConfirmButton();
        const checkbox = document.getElementById("deleteCheckbox");
        confirmButton.disabled = true;
        checkbox.addEventListener("change", () => {
          confirmButton.disabled = !checkbox.checked;
        });
      },
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await dispatch(deleteAdminCommissionStructure({
          structureId: structure.id,
          nationalId: structure.nationalId,
        }));

        Swal.fire({
          title: "Deleted!",
          text: "Commission structure deleted successfully",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });

        await fetchCommissionStructures();
      } catch (error) {
        console.error("Error deleting commission structure:", error);
        Swal.fire({
          title: "Error",
          text: "Failed to delete commission structure",
          icon: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const getUserDisplayName = (userId) => {
    const user = dashboardUsers.find(u => u.id === userId);
    return user ? `${user.firstName} ${user.lastName} (${user.email})` : userId;
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case DASHBOARD_USER_ROLES.NATIONAL:
        return <Badge color="primary" pill>N</Badge>;
      case DASHBOARD_USER_ROLES.GA:
        return <Badge color="info" pill>GA</Badge>;
      case DASHBOARD_USER_ROLES.BROKER:
        return <Badge color="success" pill>B</Badge>;
      default:
        return <Badge color="secondary" pill>?</Badge>;
    }
  };

  // Security check: Only allow admin users
  if (!isCurrentUserAdmin) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="xl">
      <ModalHeader toggle={handleClose}>
        Commission Structures Management
      </ModalHeader>
      <ModalBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            {showCreateForm
              ? (editingStructure ? "Edit Commission Structure" : "Create New Commission Structure")
              : "Commission Structures"
            }
          </h5>
          {!showCreateForm && (
            <Button
              color="primary"
              onClick={() => setShowCreateForm(true)}
              disabled={loading}
            >
              <i className="ri-add-line me-1"></i>
              Create New
            </Button>
          )}
        </div>

        {formErrors.total && (
          <Alert color="danger">
            {formErrors.total}
          </Alert>
        )}

        {showCreateForm ? (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Structure Name *</Label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    invalid={!!formErrors.name}
                    placeholder="e.g., High Broker Commission"
                  />
                  {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    Is Default Structure
                    <Input
                      type="checkbox"
                      name="isDefault"
                      checked={formData.isDefault}
                      onChange={handleInputChange}
                      className="ms-2"
                    />
                  </Label>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label>National Amount (cents) *</Label>
                  <Input
                    type="number"
                    name="national"
                    value={formData.national}
                    onChange={handleInputChange}
                    invalid={!!formErrors.national}
                    placeholder="300"
                    min="0"
                  />
                  {formErrors.national && <div className="invalid-feedback">{formErrors.national}</div>}
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>GA Amount (cents) *</Label>
                  <Input
                    type="number"
                    name="ga"
                    value={formData.ga}
                    onChange={handleInputChange}
                    invalid={!!formErrors.ga}
                    placeholder="300"
                    min="0"
                  />
                  {formErrors.ga && <div className="invalid-feedback">{formErrors.ga}</div>}
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Broker Amount (cents) *</Label>
                  <Input
                    type="number"
                    name="broker"
                    value={formData.broker}
                    onChange={handleInputChange}
                    invalid={!!formErrors.broker}
                    placeholder="400"
                    min="0"
                  />
                  {formErrors.broker && <div className="invalid-feedback">{formErrors.broker}</div>}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>National User *</Label>
                  <Input
                    type="select"
                    name="nationalId"
                    value={formData.nationalId}
                    onChange={handleInputChange}
                    invalid={!!formErrors.nationalId}
                  >
                    <option value="">Select National User</option>
                    {dashboardUsers
                      .filter(user => user.role === DASHBOARD_USER_ROLES.NATIONAL)
                      .map(user => (
                        <option key={user.id} value={user.id}>
                          {user.firstName} {user.lastName} ({user.email})
                        </option>
                      ))
                    }
                  </Input>
                  {formErrors.nationalId && <div className="invalid-feedback">{formErrors.nationalId}</div>}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Dashboard User *</Label>
                  <Input
                    type="select"
                    name="dashboardUserId"
                    value={formData.dashboardUserId}
                    onChange={handleInputChange}
                    invalid={!!formErrors.dashboardUserId}
                  >
                    <option value="">Select Dashboard User</option>
                    {dashboardUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.email})
                      </option>
                    ))}
                  </Input>
                  {formErrors.dashboardUserId && <div className="invalid-feedback">{formErrors.dashboardUserId}</div>}
                </FormGroup>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2">
              <Button type="button" color="secondary" onClick={resetForm} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={loading}>
                {loading ? <Spinner size="sm" className="me-1" /> : null}
                {editingStructure ? "Update" : "Create"} Structure
              </Button>
            </div>
          </Form>
        ) : (
          <>
            {loading ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
                <div className="mt-2">Loading commission structures...</div>
              </div>
            ) : (
              <Table responsive striped>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>National</th>
                    <th>GA</th>
                    <th>Broker</th>
                    <th>National User</th>
                    <th>Dashboard User</th>
                    <th>Default</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {commissionStructures.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4 text-muted">
                        No commission structures found
                      </td>
                    </tr>
                  ) : (
                    commissionStructures.map((structure) => (
                      <tr key={structure.id}>
                        <td>
                          <strong>{structure.name}</strong>
                          {structure.isDefault && (
                            <Badge color="warning" pill className="ms-2">Default</Badge>
                          )}
                        </td>
                        <td>${formatCentsToDollars(structure.national)}</td>
                        <td>${formatCentsToDollars(structure.ga)}</td>
                        <td>${formatCentsToDollars(structure.broker)}</td>
                        <td>{getUserDisplayName(structure.nationalId)}</td>
                        <td>{getUserDisplayName(structure.dashboardUserId)}</td>
                        <td>
                          {structure.isDefault ? (
                            <Badge color="success" pill>Yes</Badge>
                          ) : (
                            <Badge color="secondary" pill>No</Badge>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              color="soft-primary"
                              size="sm"
                              onClick={() => handleEdit(structure)}
                              disabled={loading}
                            >
                              <i className="ri-pencil-line"></i>
                            </Button>
                            <Button
                              color="soft-danger"
                              size="sm"
                              onClick={() => handleDelete(structure)}
                              disabled={loading}
                            >
                              <i className="ri-delete-bin-line"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={loading}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AdminCommissionStructuresModal;